"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.daysRemaining = daysRemaining;
exports.clockStatusForDeadline = clockStatusForDeadline;
exports.clockLimitStatus = clockLimitStatus;
exports.overallClockStatus = overallClockStatus;
// Read-time derivation shared by the resolver and UI copy — kept separate
// from computeContainerClock so callers can re-derive status from a stored
// deadline without re-running the whole rule engine.
const date_1 = require("encamp-shared/dist/utils/date");
const types_1 = require("./rules/types");
// Positive when the deadline is in the future.
// Retaining this seemingly pointless function wrapping another function
// in one line as a single point of editing whether the clock engine
// counts days as calendar days or down-to-the-millisecond days
function daysRemaining(deadline, now) {
    return (0, date_1.utcDaysBetween)(now, deadline);
}
function clockStatusForDeadline(deadline, now, approachingWindowDays = types_1.APPROACHING_WINDOW_DAYS) {
    if (deadline === null) {
        return "NONE";
    }
    const remaining = daysRemaining(deadline, now);
    if (remaining < 0) {
        return "OVERDUE";
    }
    if (remaining <= approachingWindowDays) {
        return "APPROACHING";
    }
    return "OK";
}
/**
 * Status of ONE computed limit. TIME limits go through the deadline buckets —
 * except a satellite move window, which is a short legal deadline with no calm
 * state, so it reads APPROACHING from the moment it exists and OVERDUE once
 * missed. A NONE limit is a citable "no limit applies," which is status NONE,
 * not OK: OK would claim a limit is being satisfied. VOLUME limits read the
 * area's cached verdicts; OVER_LIMIT wins over UNDETERMINED because a proven
 * exceedance stands whatever the skipped members would add.
 */
function clockLimitStatus(limit, area, now) {
    if (limit.limitType === "TIME") {
        const approachingWindowDays = limit.isMoveWindow ? Number.POSITIVE_INFINITY : undefined;
        return clockStatusForDeadline(limit.deadlineDate, now, approachingWindowDays);
    }
    if (limit.limitType === "NONE") {
        return "NONE";
    }
    if (area?.isOverThreshold)
        return "OVER_LIMIT";
    return area?.volumeUndetermined ? "UNDETERMINED" : "OK";
}
// A breached volume cap outranks an approaching deadline, and a missed
// deadline outranks both: an over-threshold satellite whose 3-day move
// deadline has passed reads OVERDUE, since the passed deadline is the harder
// fact and the volume breach is what created it. UNDETERMINED needs attention
// but yields to any dated fact, so it sits between OK and APPROACHING.
const CLOCK_STATUS_RANK = {
    NONE: 0,
    OK: 1,
    UNDETERMINED: 2,
    APPROACHING: 3,
    OVER_LIMIT: 4,
    OVERDUE: 5,
};
/**
 * The chip's single answer: the worst status across a computed clock's
 * limits, NONE when there are no limits at all. This is THE definition of a
 * container's overall clock status — the GraphQL resolver renders it and the
 * SQL `clock:` bucket filter mirrors it, so any other encoding of "which
 * status wins" is a drift bug.
 */
function overallClockStatus(limits, area, now) {
    return limits.reduce((worst, limit) => {
        const status = clockLimitStatus(limit, area, now);
        return CLOCK_STATUS_RANK[status] > CLOCK_STATUS_RANK[worst] ? status : worst;
    }, "NONE");
}
//# sourceMappingURL=clockStatus.js.map